import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import { Box, Button, DialogContent, Stack } from '@mui/material';
import type { CSSProperties, IframeHTMLAttributes, ReactNode } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

type PDFViewerProps = Omit<IframeHTMLAttributes<HTMLIFrameElement>, 'ref'> & {
  src?: string;
  containerStyle?: CSSProperties;
};

const applyDefaultPdfHash = (src?: string) => {
  if (!src) return src;
  if (src.includes('#')) return src;
  return `${src}#navpanes=0`;
};

export const PDFViewer = ({ src, style, containerStyle, ...iframeProps }: PDFViewerProps) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const onChange = () => setIsFullscreen(document.fullscreenElement === wrapperRef.current);
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  const toggleFullscreen = useCallback(() => {
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      wrapperRef.current?.requestFullscreen?.();
    }
  }, []);

  const resolvedSrc = applyDefaultPdfHash(src);

  return (
    <Box
      ref={wrapperRef}
      sx={{
        position: 'relative',
        width: '100%',
        height: '100%',
        minHeight: 600,
        bgcolor: 'background.paper',
        ...containerStyle
      }}
    >
      <iframe
        {...iframeProps}
        src={resolvedSrc}
        style={{
          width: '100%',
          height: '100%',
          minWidth: 100,
          minHeight: 600,
          border: 'none',
          display: 'block',
          ...style
        }}
      />
      <Button
        variant="contained"
        size="small"
        onClick={toggleFullscreen}
        startIcon={isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
        sx={{
          position: 'absolute',
          top: 12,
          right: '20%',
          zIndex: 2,
          textTransform: 'none',
          fontWeight: 600,
          boxShadow: 2
        }}
      >
        {isFullscreen ? 'Exit Full Screen' : 'Show Full Screen'}
      </Button>
    </Box>
  );
};

export const PDF_DIALOG_PAPER_PROPS = {
  sx: { width: '90vw', height: '90vh', maxWidth: 'none', maxHeight: 'none' }
} as const;

type PdfDialogFrameProps = {
  toolbar?: ReactNode;
  header?: ReactNode;
  children: ReactNode;
};

export const PdfDialogFrame = ({ toolbar, header, children }: PdfDialogFrameProps) => (
  <DialogContent sx={{ overflowY: 'hidden', display: 'flex', flexDirection: 'column', flex: 1 }}>
    <Stack sx={{ flex: 1, minHeight: 0 }}>
      {toolbar && (
        <Stack
          sx={{
            width: '100%',
            flexDirection: 'row',
            justifyContent: 'flex-end',
            alignItems: 'flex-start',
            m: 1
          }}
        >
          {toolbar}
        </Stack>
      )}
      {header}
      <Stack
        sx={{
          width: '100%',
          border: '1px solid #ccc',
          flex: 1,
          minHeight: 0,
          justifyContent: 'stretch',
          alignItems: 'stretch'
        }}
      >
        {children}
      </Stack>
    </Stack>
  </DialogContent>
);

export default PDFViewer;
